import polylabel from 'polylabel'
import { Vector3 } from 'three'

import { findCenter } from './Points'
import { alignToZAxis } from './Shape'

/**
 * Find the centroid of a polygon defined by an array of points.
 * In the case of a concave polygon, polylabel will be used to ensure
 * the centroid lies within the polygon.
 *
 * @param points
 */
export const findPolygonCentroid = (points: Vector3[]): Vector3 => {
  // If the polygon is convex, we can use simple center calculation
  const { points: zAlignedPosV3, inverseMatrix, offset } = alignToZAxis(points, true)
  if (isConvexPolygon(zAlignedPosV3)) {
    const center = findCenter(points)
    if (center) return center
  }

  const centered = polylabel([zAlignedPosV3.map((pos) => [pos.x, pos.y])], 0.001)
  return new Vector3(centered[0], centered[1], points[0].z).applyMatrix4(inverseMatrix).add(offset)
}

/**
 * Helper function to check if two line segments intersect.
 *
 * @param p1 Start point of first line segment
 * @param p2 End point of first line segment
 * @param p3 Start point of second line segment
 * @param p4 End point of second line segment
 * @returns True if the line segments intersect, false otherwise
 */
const linesIntersect = (p1: Vector3, p2: Vector3, p3: Vector3, p4: Vector3): boolean => {
  const d1 = direction(p3, p4, p1)
  const d2 = direction(p3, p4, p2)
  const d3 = direction(p1, p2, p3)
  const d4 = direction(p1, p2, p4)

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true
  }

  return false
}

/**
 * Helper function to calculate the direction of the turn formed by three points.
 *
 * @param pi First point
 * @param pj Second point
 * @param pk Third point
 * @returns Positive value for counter-clockwise turn, negative for clockwise, zero for collinear
 */
const direction = (pi: Vector3, pj: Vector3, pk: Vector3): number =>
  (pj.x - pi.x) * (pk.y - pi.y) - (pk.x - pi.x) * (pj.y - pi.y)

/**
 * Checks if any lines in a polygon intersect with each other.
 *
 * @param points Array of Vector3 points representing the vertices of the polygon
 * @returns True if any lines intersect, false otherwise
 */
export const checkForLineIntersections = (points: Vector3[]): boolean => {
  const numPoints = points.length

  for (let i = 0; i < numPoints; i += 1) {
    const a1 = points[i]
    const a2 = points[(i + 1) % numPoints]

    for (let j = i + 1; j < numPoints; j += 1) {
      // Check non-adjacent segments only
      if (!(j === i || j === (i + 1) % numPoints || (i === 0 && j === numPoints - 1))) {
        const b1 = points[j]
        const b2 = points[(j + 1) % numPoints]

        if (linesIntersect(a1, a2, b1, b2)) {
          return true
        }
      }
    }
  }

  return false
}

/**
 * Checks if a polygon is convex in the XY plane.
 * Note: This function only considers the x and y coordinates of the points.
 * For 3D polygons on arbitrary planes, first align them to the XY plane using alignToZAxis().
 * @param points Array of Vector3 points representing the vertices of the polygon
 * @returns True if the polygon is convex in the XY plane, false otherwise
 */
export const isConvexPolygon = (points: Vector3[]): boolean => {
  const numPoints = points.length
  if (numPoints < 4) return true // Triangles are always convex

  let sign = 0

  for (let i = 0; i < numPoints; i += 1) {
    const dx1 = points[(i + 2) % numPoints].x - points[(i + 1) % numPoints].x
    const dy1 = points[(i + 2) % numPoints].y - points[(i + 1) % numPoints].y
    const dx2 = points[i].x - points[(i + 1) % numPoints].x
    const dy2 = points[i].y - points[(i + 1) % numPoints].y
    const zCrossProduct = dx1 * dy2 - dy1 * dx2

    if (zCrossProduct !== 0) {
      if (sign === 0) {
        sign = zCrossProduct > 0 ? 1 : -1
      } else if ((zCrossProduct > 0 && sign < 0) || (zCrossProduct < 0 && sign > 0)) {
        return false
      }
    }
  }

  return true
}

/**
 * Checks if a polygon is concave.
 * @param points Array of Vector3 points representing the vertices of the polygon
 * @returns True if the polygon is concave, false otherwise
 */
export const isConcavePolygon = (points: Vector3[]): boolean => !isConvexPolygon(points)

/**
 * Compute a polygon's normal with Newell's method.
 *
 * Averages over every edge rather than taking the cross product of the first three vertices,
 * which is unreliable when those happen to be near-collinear or when the polygon was drawn
 * across a surface that is not quite flat.
 *
 * @param points Polygon vertices in order.
 */
export const computePolygonNormal = (points: Vector3[]): Vector3 => {
  const normal = new Vector3()

  for (let i = 0; i < points.length; i += 1) {
    const current = points[i]
    const next = points[(i + 1) % points.length]

    normal.x += (current.y - next.y) * (current.z + next.z)
    normal.y += (current.z - next.z) * (current.x + next.x)
    normal.z += (current.x - next.x) * (current.y + next.y)
  }

  return normal.lengthSq() > 0 ? normal.normalize() : new Vector3(0, 0, 1)
}

/**
 * Test whether a point lies inside a polygon drawn in 3D space.
 *
 * The polygon is treated as its own outline, concavity included — unlike a bounding box, which
 * would also cover the space between a concave shape's arms and everything out to its corners.
 * The test projects both the polygon and the point onto the polygon's plane and runs an
 * even-odd ray crossing there.
 *
 * @param point Point to test.
 * @param points Polygon vertices in order. The polygon is treated as closed.
 * @param planeTolerance Optional limit on how far off the polygon's plane the point may sit.
 *                       Without it, anything projecting inside the outline counts, however
 *                       far in front of or behind the polygon it is.
 */
export const isPointInPolygon = (point: Vector3, points: Vector3[], planeTolerance?: number): boolean => {
  if (points.length < 3) return false

  const normal = computePolygonNormal(points)
  const origin = points[0]
  const offset = point.clone().sub(origin)

  if (planeTolerance !== undefined && Math.abs(offset.dot(normal)) > planeTolerance) return false

  // Any in-plane basis will do — containment does not depend on how the plane is oriented.
  let u: Vector3 | undefined
  for (let i = 1; i < points.length && !u; i += 1) {
    const edge = points[i].clone().sub(origin)
    const inPlane = edge.sub(normal.clone().multiplyScalar(edge.dot(normal)))
    if (inPlane.lengthSq() > 0) u = inPlane.normalize()
  }
  if (!u) return false

  const v = normal.clone().cross(u).normalize()

  const projected = points.map((vertex) => {
    const local = vertex.clone().sub(origin)
    return { x: local.dot(u), y: local.dot(v) }
  })
  const target = { x: offset.dot(u), y: offset.dot(v) }

  let isInside = false
  for (let i = 0, j = projected.length - 1; i < projected.length; j = i, i += 1) {
    const a = projected[i]
    const b = projected[j]

    const straddlesRay = a.y > target.y !== b.y > target.y
    if (straddlesRay && target.x < ((b.x - a.x) * (target.y - a.y)) / (b.y - a.y) + a.x) {
      isInside = !isInside
    }
  }

  return isInside
}
